import { Product, InsertSet, Card } from 'shared/models';
import productService from './productService';
import insertSetService from './insertSetService';
import cardService from './cardService';

const slugService = {
  // Get product by extracting ID from slug and delegating to productService
  getProductBySlug: async (productSlug) => {
    // Extract ID from slug
    const parsedSlug = slugService.parseSlugWithId(productSlug);
    
    if (!parsedSlug.id) {
      throw new Error('Could not extract ID from product slug: ' + productSlug);
    }
    
    // Use productService to get the product by ID
    return await productService.getProductById(parsedSlug.id);
  },
  
  // Get insert set by extracting ID from slug and delegating to insertSetService
  getInsertSetBySlug: async (productSlug, insertSetSlug) => {
    // Extract ID from slug
    const parsedSlug = slugService.parseSlugWithId(insertSetSlug);
    
    if (!parsedSlug.id) {
      throw new Error('Could not extract ID from insert set slug: ' + insertSetSlug);
    }
    
    // Use insertSetService to get the insert set by ID
    return await insertSetService.getInsertSetById(parsedSlug.id);
  },
  
  // Get card by extracting ID from slug and delegating to cardService
  getCardBySlug: async (cardSlug) => {
    // Extract ID from slug
    const parsedSlug = slugService.parseSlugWithId(cardSlug);
    
    if (!parsedSlug.id) {
      throw new Error('Could not extract ID from card slug: ' + cardSlug);
    }
    
    // Use cardService to get the card by ID
    return await cardService.getCardById(parsedSlug.id);
  },
  
  // Create URL-friendly slug from text
  createSlug: (text) => {
    if (!text) return '';
    
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')        // Replace spaces with -
      .replace(/&/g, '-and-')      // Replace & with 'and'
      .replace(/[^\w\-]+/g, '')    // Remove all non-word characters
      .replace(/\-\-+/g, '-')      // Replace multiple - with single -
      .replace(/^-+/, '')          // Trim - from start of text
      .replace(/-+$/, '');         // Trim - from end of text
  },
  
  // Get product URL (SEO-friendly with ID only where needed)
  getProductUrl: (product) => {
    if (!product || !product.id) return '';
    
    // Create or use existing slug
    const slugPart = product.slug || slugService.createProductSlug(product);
    
    // Return URL with object ID embedded at the end - legacy format, kept for reference
    // Moving forward, use getTradingCardSetUrl instead
    return `/trading-card-set/${slugPart}-${product.id}`;
  },
  
  // Get trading card set URL (SEO-friendly with ID only where needed)
  getTradingCardSetUrl: (product) => {
    if (!product || !product.id) return '';
    
    // Create or use existing slug
    const slugPart = product.slug || slugService.createProductSlug(product);
    
    // Return URL with object ID embedded at the end
    return `/trading-card-set/${slugPart}-${product.id}`;
  },
  
  // Create a product slug with year handling
  createProductSlug: (product) => {
    if (!product) return '';
    
    const productName = product.name || '';
    const productYear = product.year ? product.year.toString() : '';
    
    // Check if product name already starts with the year
    const startsWithYear = productName.startsWith(productYear);
    
    if (startsWithYear || !productYear) {
      // If name already has year prefix or no year available, just use name
      return slugService.createSlug(productName);
    } else {
      // Otherwise, prefix with year
      return slugService.createSlug(productYear + '-' + productName);
    }
  },
  
  // Get insert set URL (SEO-friendly with product name, insert set name and ID)
  getInsertSetUrl: (insertSet, product) => {
    if (!insertSet || !insertSet.id) return '';
    if (!product) return '';
    
    // Create or use existing slugs
    const productSlug = product.slug || slugService.createProductSlug(product);
    const insertSetSlug = insertSet.slug || slugService.createSlug(insertSet.name);
    
    // Return URL with new pattern: /trading-card-set/productName/insertSetName-id
    return `/trading-card-set/${productSlug}/${insertSetSlug}-${insertSet.id}`;
  },
  
  // Get card URL (SEO-friendly with product, insert set, card number, name and ID)
  getCardUrl: (card, insertSet, product) => {
    if (!card || !card.id || !insertSet || !product) return '';
    
    // Create slugs for product, insert set, and card
    const productSlug = product.slug || slugService.createProductSlug(product);
    const insertSetSlug = insertSet.slug || slugService.createSlug(insertSet.name);
    
    // Create card slug using card number if available, otherwise just use name
    const cardNameSlug = slugService.createSlug(card.name || '');
    const cardNumberPart = card.cardNumber ? `${card.cardNumber}-` : '';
    
    // Return URL with new pattern: /trading-card-set/productSlug/insertSetSlug/cardNumber-cardName-id
    return `/trading-card-set/${productSlug}/${insertSetSlug}/${cardNumberPart}${cardNameSlug}-${card.id}`;
  },
  
  // Get user's collection product URL
  getUserCollectionProductUrl: (product, username) => {
    if (!product || !product.id || !username) return '';
    
    // Create or use existing slug
    const slugPart = product.slug || slugService.createProductSlug(product);
    
    // Return URL with username and product slug/ID
    return `/@${username}/collection/${slugPart}-${product.id}`;
  },
  
  // Get user's collection insert set URL
  getUserCollectionInsertSetUrl: (insertSet, product, username) => {
    if (!insertSet || !insertSet.id || !product || !username) return '';
    
    // Create or use existing slugs
    const productSlug = product.slug || slugService.createProductSlug(product);
    const insertSetSlug = insertSet.slug || slugService.createSlug(insertSet.name);
    
    // Return URL with username, product slug, and insert set slug/ID
    return `/@${username}/collection/${productSlug}/${insertSetSlug}-${insertSet.id}`;
  },
  
  // Get user's collection card URL
  getUserCollectionCardUrl: (card, insertSet, product, username) => {
    if (!card || !card.id || !insertSet || !product || !username) return '';
    
    // Create slugs for product, insert set, and card
    const productSlug = product.slug || slugService.createProductSlug(product);
    const insertSetSlug = insertSet.slug || slugService.createSlug(insertSet.name);
    
    // Create card slug using card number if available, otherwise just use name
    const cardNameSlug = slugService.createSlug(card.name || '');
    const cardNumberPart = card.cardNumber ? `${card.cardNumber}-` : '';
    
    // Return URL with username, product slug, insert set slug, and card slug/ID
    return `/@${username}/collection/${productSlug}/${insertSetSlug}/${cardNumberPart}${cardNameSlug}-${card.id}`;
  },
  
  // Get user's wishlist product URL
  getUserWishlistProductUrl: (product, username) => {
    if (!product || !product.id || !username) return '';
    
    // Create or use existing slug
    const slugPart = product.slug || slugService.createProductSlug(product);
    
    // Return URL with username and product slug/ID
    return `/@${username}/wishlist/${slugPart}-${product.id}`;
  },
  
  // Get user's wishlist insert set URL
  getUserWishlistInsertSetUrl: (insertSet, product, username) => {
    if (!insertSet || !insertSet.id || !product || !username) return '';
    
    // Create or use existing slugs
    const productSlug = product.slug || slugService.createProductSlug(product);
    const insertSetSlug = insertSet.slug || slugService.createSlug(insertSet.name);
    
    // Return URL with username, product slug, and insert set slug/ID
    return `/@${username}/wishlist/${productSlug}/${insertSetSlug}-${insertSet.id}`;
  },
  
  // Get user's wishlist card URL
  getUserWishlistCardUrl: (card, insertSet, product, username) => {
    if (!card || !card._id || !insertSet || !product || !username) return '';
    
    // Create slugs for product, insert set, and card
    const productSlug = product.slug || slugService.createProductSlug(product);
    const insertSetSlug = insertSet.slug || slugService.createSlug(insertSet.name);
    
    // Create card slug using card number if available, otherwise just use name
    const cardNameSlug = slugService.createSlug(card.name || '');
    const cardNumberPart = card.cardNumber ? `${card.cardNumber}-` : '';
    
    // Return URL with username, product slug, insert set slug, and card slug/ID
    return `/@${username}/wishlist/${productSlug}/${insertSetSlug}/${cardNumberPart}${cardNameSlug}-${card._id}`;
  },
  
  // Get user's available cards product URL
  getUserAvailableCardsProductUrl: (product, username) => {
    if (!product || !product.id || !username) return '';
    
    // Create or use existing slug
    const slugPart = product.slug || slugService.createProductSlug(product);
    
    // Return URL with username and product slug/ID
    return `/@${username}/available-cards/${slugPart}-${product.id}`;
  },
  
  // Get user's available cards insert set URL
  getUserAvailableCardsInsertSetUrl: (insertSet, product, username) => {
    if (!insertSet || !insertSet.id || !product || !username) return '';
    // Create or use existing slugs
    const productSlug = product.slug || slugService.createProductSlug(product);
    const insertSetSlug = insertSet.slug || slugService.createSlug(insertSet.name);
    
    // Return URL with username, product slug, and insert set slug/ID
    return `/@${username}/available-cards/${productSlug}/${insertSetSlug}-${insertSet.id}`;
  },
  
  // Get user's available cards card URL
  getUserAvailableCardsCardUrl: (card, insertSet, product, username) => {
    // Use either _id or id property of the card
    const cardId = card?._id || card?.id;

    if (!card || !cardId || !insertSet || !product || !username) return '';
    // Create slugs for product, insert set, and card
    const productSlug = product.slug || slugService.createProductSlug(product);
    const insertSetSlug = insertSet.slug || slugService.createSlug(insertSet.name);
    
    // Create card slug using card number if available, otherwise just use name
    const cardNameSlug = slugService.createSlug(card.name || '');
    const cardNumberPart = card.cardNumber ? `${card.cardNumber}-` : '';
    
    // Return URL with username, product slug, insert set slug, and card slug/ID
    return `/@${username}/available-cards/${productSlug}/${insertSetSlug}/${cardNumberPart}${cardNameSlug}-${cardId}`;
  },
  
  // Parse a slug-id format to extract the ID
  parseSlugWithId: (slugWithId) => {
    if (!slugWithId) return null;
    
    // Look for MongoDB ObjectId pattern at the end (24 hex chars)
    const idMatch = slugWithId.match(/-([0-9a-f]{24})$/);
    
    // If we found an ID at the end with a hyphen before it
    if (idMatch && idMatch[1]) {
      const id = idMatch[1];
      const slug = slugWithId.substring(0, slugWithId.length - id.length - 1); // Remove ID and hyphen
      
      return {
        slug: slug,
        id: id
      };
    }
    
    // If it doesn't fit our pattern, just return the original as the slug (ID can be null)
    // This handles legacy URLs and URLs without embedded IDs
    return {
      slug: slugWithId,
      id: null
    };
  }
};

export default slugService;